import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { api } from "@/lib/api"

// ───────────────────────────────────────────────────────────────────────
// Backend contracts (V-I admin endpoints — legacy /admin/* envelope):
//
//   POST /admin/message_list  { page, limit, search?, sortyBy?, sortDirection? }
//                            → { success, data: { docs: AdminMessageBatch[], totalDocs, ... }, message }
//
//   POST /admin/message_send  { coach_id, kind, target, user_ids?, topic?, body?, preview?, mediaUrl?, mediaKey?, durationSec? }
//                            → { success, data: { batch_id, recipientCount }, message }
//
//   POST /admin/message_get    { batch_id }
//                             → { success, data: { rows }, message }
//   POST /admin/message_delete { batch_id }
//                             → { success, data: { deletedCount }, message }
//
// One "batch" = one admin send event, fanned out N ways. The list
// endpoint aggregates rows back into batches so the table shows ONE
// row per send (not N).
// ───────────────────────────────────────────────────────────────────────

export type MessageKind = "voice" | "quote" | "text" | "short"
export type MessageTarget = "followers" | "specific" | "both"

export type AdminMessageBatch = {
  /** batch_id (shared by every row in the send) */
  _id: string
  coach_id: string
  coach_name?: string
  coach_avatar_url?: string
  kind: MessageKind
  topic?: string
  body?: string
  preview?: string
  mediaUrl?: string
  durationSec?: number
  sent_at?: string
  recipientCount: number
  readCount: number
  /** V-CoachSub Phase 3: persisted tier; seeds the edit-modal radio
   *  so an unrelated edit doesn't silently flip premium → general. */
  content_tier?: "general" | "premium"
}

type MessageListParams = {
  page: number
  limit: number
  search?: string
  /** Backend typo preserved: extra "y". */
  sortyBy?: string
  sortDirection?: 1 | -1
}

export type MessageListResponse = {
  docs: AdminMessageBatch[]
  totalDocs: number
  page: number
  limit: number
  totalPages: number
}

export function useMessageBatchesList(params: MessageListParams) {
  return useQuery({
    queryKey: ["admin", "messages", "list", params] as const,
    queryFn: async (): Promise<MessageListResponse> => {
      const res = await api.post<{
        success: boolean
        data: MessageListResponse
        message: string
      }>("/admin/message_list", params)
      const data = res.data?.data
      return {
        docs: Array.isArray(data?.docs) ? data.docs : [],
        totalDocs: typeof data?.totalDocs === "number" ? data.totalDocs : 0,
        page: typeof data?.page === "number" ? data.page : params.page,
        limit: typeof data?.limit === "number" ? data.limit : params.limit,
        totalPages: typeof data?.totalPages === "number" ? data.totalPages : 0,
      }
    },
    placeholderData: (prev) => prev,
  })
}

export type SendMessagePayload = {
  coach_id: string
  kind: MessageKind
  target: MessageTarget
  user_ids?: string[]
  topic?: string
  body?: string
  preview?: string
  mediaUrl?: string
  mediaKey?: string
  /** V-N shorts: poster frame uploaded separately and supplied alongside
   *  the mp4. Required when kind === "short". */
  coverUrl?: string
  coverKey?: string
  durationSec?: number
  /** V-CoachSub Phase 3: general = universally accessible;
   *  premium = locked to viewers with an active per-coach sub to
   *  this coach. Default "general" when omitted. */
  content_tier?: "general" | "premium"
}

export type SendMessageResponse = {
  success: boolean
  data?: { batch_id: string | null; recipientCount: number }
  message: string
}

export function useSendMessage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (
      payload: SendMessagePayload
    ): Promise<SendMessageResponse> => {
      const res = await api.post<SendMessageResponse>(
        "/admin/message_send",
        payload
      )
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "messages"] })
    },
  })
}

export function useDeleteMessageBatch() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (batch_id: string) => {
      const res = await api.post("/admin/message_delete", { batch_id })
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "messages"] })
    },
  })
}

export type UpdateMessagePayload = {
  batch_id: string
  topic?: string
  body?: string
  preview?: string
  mediaUrl?: string
  mediaKey?: string
  coverUrl?: string
  coverKey?: string
  durationSec?: number
  /** V-CoachSub Phase 3 UX: admin can flip an existing message's
   *  paywall tier from the edit modal. Default 'general' on the
   *  server side when omitted. */
  content_tier?: "general" | "premium"
}

export function useUpdateMessageBatch() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (
      payload: UpdateMessagePayload
    ): Promise<{ success: boolean; message: string }> => {
      const res = await api.post<{ success: boolean; message: string }>(
        "/admin/message_update",
        payload
      )
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "messages"] })
    },
  })
}

export function useGetMessageBatch(batchId: string | undefined) {
  return useQuery({
    queryKey: ["admin", "messages", "get", batchId] as const,
    queryFn: async (): Promise<unknown[]> => {
      if (!batchId) return []
      const res = await api.post<{
        success: boolean
        data: { rows: unknown[] }
        message: string
      }>("/admin/message_get", { batch_id: batchId })
      return Array.isArray(res.data?.data?.rows) ? res.data.data.rows : []
    },
    enabled: !!batchId,
  })
}
